// src/components/discussion/render.jsx
import { findLinks } from "../../utils/links";

/**
 * Подсвечивает вхождения query в тексте. Если activeIndex задан -
 * именно это вхождение получает дополнительный класс (активное
 * совпадение в навигации). Индексация - по порядку появления в тексте.
 */
export function highlightText(text, query, activeIndex = null) {
  if (!query || !query.trim()) return text;
  const lower = query.toLowerCase();
  const regex = new RegExp(
    `(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi",
  );

  let occurrence = 0;
  return text.split(regex).map((part, i) => {
    if (part.toLowerCase() !== lower) return part;
    const isActive = activeIndex === occurrence;
    occurrence += 1;
    return (
      <mark
        key={i}
        className={
          isActive
            ? "search-highlight search-highlight-active"
            : "search-highlight"
        }
      >
        {part}
      </mark>
    );
  });
}

export function renderMentionText(text) {
  return text.split("@").map((part, i) => {
    if (i === 0) return <span key={i}>{part}</span>;
    const tokens = part.split(/(\s+)/);
    let mention = tokens[0];
    let restStart = 1;
    if (tokens.length > 2 && /^[А-ЯA-ZЁ]/.test(tokens[2])) {
      mention += " " + tokens[2];
      restStart = 3;
    }
    return (
      <span key={i}>
        <span className="mention">@{mention}</span>
        {tokens.slice(restStart).join("")}
      </span>
    );
  });
}

/**
 * Разрезает текст комментария на непересекающиеся React-узлы:
 *   - http(s):// - <a target="_blank" rel="noopener noreferrer">;
 *   - mailto: и bare-email - <a href="mailto:…"> без target;
 *   - Windows-пути (kind === 'path') - <button className="link">
 *     с onClick={onPathClick}. Браузеры блокируют переход по file://
 *     со страниц http(s), поэтому вместо перехода - колбэк:
 *     в проекте копирование пути в буфер обмена;
 *   - @упоминания и подсветка поиска - в оставшихся кусках.
 *
 * Ссылки и email «выигрывают» у @упоминаний и подсветки: фрагмент,
 * распознанный как ссылка, целиком уходит в узел. Внутрь <a>/<button>
 * поисковая подсветка не применяется - узел должен остаться единым,
 * иначе клик сломается.
 *
 * onPathClick - коллбэк (path) => void. Если не передан, path
 * рендерится как disabled-кнопка: визуально ссылка, но клик ничего
 * не делает. Обработчик не обязателен для самого факта отрисовки -
 * так renderTextWithLinks остаётся чистым представлением.
 *
 * Возвращает массив React-узлов.
 */
export function renderTextWithLinks(text, { searchQuery, onPathClick } = {}) {
  if (!text) return text;
  const str = String(text);
  const links = findLinks(str);

  if (links.length === 0) {
    return searchQuery && searchQuery.trim()
      ? highlightText(str, searchQuery.trim())
      : renderMentionText(str);
  }

  const parts = [];
  let cursor = 0;

  for (let i = 0; i < links.length; i++) {
    const link = links[i];
    if (link.index > cursor) {
      parts.push(
        renderSlice(str.slice(cursor, link.index), searchQuery, `t-${i}`),
      );
    }

    if (link.kind === "path") {
      parts.push(
        <button
          key={`l-${i}`}
          type="button"
          className="link"
          onClick={onPathClick ? () => onPathClick(link.url) : undefined}
          disabled={!onPathClick}
          title={onPathClick ? "Скопировать путь" : undefined}
        >
          {link.display}
        </button>,
      );
    } else {
      const isExternal = link.kind === "url";
      parts.push(
        <a
          key={`l-${i}`}
          className="link"
          href={link.url}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
        >
          {link.display}
        </a>,
      );
    }
    cursor = link.index + link.length;
  }

  if (cursor < str.length) {
    parts.push(renderSlice(str.slice(cursor), searchQuery, "t-end"));
  }

  return parts;
}

/**
 * Нессылочный кусок текста: при наличии поискового запроса - подсветка,
 * иначе - @упоминания. Либо highlightText, либо renderMentionText, но
 * не оба сразу - как было в CommentItem до правки.
 */
function renderSlice(text, searchQuery, key) {
  if (!text) return null;
  if (searchQuery && searchQuery.trim()) {
    return <span key={key}>{highlightText(text, searchQuery.trim())}</span>;
  }
  return <span key={key}>{renderMentionText(text)}</span>;
}
